import React, { FC } from 'react';
import { NavLinkItem } from '@components/layout/Sidebar/components/NavLinkItem';
import { MessagesNavItem, PayoutRequestNavItem, RequestNavItem } from '@components/layout/Sidebar/components/customNavItems';
import { gettext, useGettext } from '@cranium/i18n';
import { IdentityTypeEnum } from '@/interfaces/brokrete';
import { usePermissions, useClientSettings, usePartnerData } from '@/shared/access';
import { get } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import { useLocalStorage } from '@/core/Storage';
import styled from 'styled-components';
import { Tooltip } from 'antd';

export interface MenuItem {
  name: string;
  settings?: {
    visible?: boolean;
    preview?: boolean;
  };
  allowed: boolean;
  withProtectedRenderer?: IdentityTypeEnum[];
  component: JSX.Element;
}

export const SettingsManager: FC<{
  settings?: {
    visible?: boolean;
    preview?: boolean;
  };
}> = ({ children, settings }) => {
  if (!canShowBySettings(settings)) return null;
  if (!settings) return <>{children}</>;
  if (!Object(settings).hasOwnProperty('visible') && !Object(settings).hasOwnProperty('preview')) return <>{children}</>;
  if (settings?.visible) return <>{children}</>;
  return (
    <Tooltip placement={'right'} destroyTooltipOnHide title={gettext('Please contact support to enable this feature')}>
      <Disabled>{children}</Disabled>
    </Tooltip>
  );
};

const Disabled = styled.div`
  position: relative;
  &:after {
    position: absolute;
    inset: 0;
    content: '';
    background: var(--white);
    opacity: 0.5;
  }
  > * {
    pointer-events: none;
    touch-action: none;
    cursor: not-allowed;
    color: var(--gray-7) !important;
  }
`;

export function canShowBySettings(settings?: { visible?: boolean; preview?: boolean }) {
  if (!settings) return true;
  if (!Object(settings).hasOwnProperty('visible') && !Object(settings).hasOwnProperty('preview')) return true;
  return settings.visible || settings?.preview;
}

type SidebarStructure = Array<
  { type: 'single'; item: MenuItem } | { type: 'grouped'; title: string; openByDefault: boolean; items: MenuItem[] }
>;

export const useSidebarStructure = (mobile?: boolean, minimizes?: boolean) => {
  const permissions = usePermissions();
  const settings = useClientSettings();
  const partnerData = usePartnerData();
  const { gettext } = useGettext();

  const { value } = useLocalStorage<string>(`token-${partnerData?.id || ''}`);
  const scheduleQuery = useLocalStorage<string>(`LAST_KNOWN_SCHEDULE_FILTERS-${partnerData?.id || ''}.v1`);

  const menuItems: Record<string, MenuItem> = {
    profile: {
      name: 'profile',
      allowed: !!mobile,
      component: (
        <NavLinkItem
          minimizes={minimizes}
          iconClass="icon-setting"
          label={gettext('Profile')}
          path="/profile"
          userTipName="sidebar.profile"
        />
      )
    },
    orders: {
      name: 'orders',
      settings: settings?.orders,
      allowed: get(permissions, 'orders.show'),
      component: (
        <NavLinkItem
          minimizes={minimizes}
          iconClass="icon-orders"
          label={gettext('Orders')}
          path="/orders"
          userTipName="sidebar.orders"
        />
      )
    },
    schedule: {
      name: 'schedule',
      settings: settings?.scheduling,
      allowed: get(permissions, 'scheduling.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem
          minimizes={minimizes}
          iconClass="icon-schedule2"
          label={gettext('Schedule')}
          path={['/schedule', scheduleQuery.value].filter(Boolean).join('')}
          userTipName="sidebar.schedule"
        />
      )
    },
    drivers: {
      name: 'drivers',
      settings: settings?.drivers,
      allowed: get(permissions, 'drivers.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem
          minimizes={minimizes}
          iconClass="icon-driver"
          label={gettext('Operators')}
          path="/operators"
          exact
          userTipName="sidebar.drivers"
        />
      )
    },
    deliveries: {
      name: 'deliveries',
      settings: settings?.driver_tasks,
      allowed: get(permissions, 'driver_tasks.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem
          minimizes={minimizes}
          iconClass="icon-time-table"
          label={gettext('Deliveries')}
          path="/deliveries"
          userTipName="sidebar.deliveries"
        />
      )
    },
    trucks: {
      name: 'trucks',
      settings: settings?.trucks,
      allowed: get(permissions, 'trucks.show'),
      component: (
        <NavLinkItem
          minimizes={minimizes}
          iconClass="icon-truck2"
          label={gettext('Trucks')}
          path="/trucks"
          userTipName="sidebar.trucks"
        />
      )
    },
    finance: {
      name: 'finance',
      settings: settings?.finances,
      allowed: get(permissions, 'finances.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: <PayoutRequestNavItem minimizes={minimizes} />
    },
    tax: {
      name: 'tax',
      settings: settings?.taxes,
      allowed: get(permissions, 'taxes.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem
          minimizes={minimizes}
          iconClass="icon-dollar-card"
          label={gettext('Tax')}
          path="/tax"
          userTipName="sidebar.tax"
        />
      )
    },
    projects: {
      name: 'projects',
      settings: settings?.projects,
      allowed: get(permissions, 'projects.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem
          minimizes={minimizes}
          iconClass="icon-project-1"
          label={gettext('Projects')}
          path="/projects"
          userTipName="sidebar.projects"
        />
      )
    },
    pallets: {
      name: 'pallets',
      settings: settings?.pallets,
      allowed: get(permissions, 'pallets.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem
          minimizes={minimizes}
          iconClass="icon-pallets"
          label={gettext('Pallets')}
          path="/pallets"
          userTipName="sidebar.pallets"
        />
      )
    },
    products: {
      name: 'products',
      settings: settings?.products,
      allowed: get(permissions, 'products.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem
          minimizes={minimizes}
          iconClass="icon-Shop"
          label={gettext('Products')}
          path="/products"
          userTipName="sidebar.products"
        />
      )
    },
    locations: {
      name: 'locations',
      settings: settings?.locations,
      allowed: get(permissions, 'locations.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem
          minimizes={minimizes}
          iconClass="icon-factory"
          label={gettext('Locations')}
          path="/locations/list"
          userTipName="sidebar.locations"
        />
      )
    },
    contractors: {
      name: 'contractors',
      settings: settings?.contractors,
      allowed: get(permissions, 'contractors.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem
          minimizes={minimizes}
          iconClass="icon-contractors"
          label={gettext('Customers')}
          path="/contractors"
          userTipName="sidebar.contractors"
        />
      )
    },
    messages: {
      name: 'messages',
      settings: settings?.messages,
      allowed: get(permissions, 'messages.show'),
      component: <MessagesNavItem minimizes={minimizes} />
    },
    availability: {
      name: 'availability',
      settings: settings?.block_off_availability,
      allowed: get(permissions, 'block_off_availability.show'),
      component: (
        <NavLinkItem
          iconClass="icon-block-off-availability"
          label={gettext('Block-Off Availability')}
          path="/availability/block-off"
          userTipName="sidebar.blockOffAvailability"
          minimizes={minimizes}
        />
      )
    },
    widgets: {
      name: 'widgets',
      settings: settings?.widgets,
      allowed: get(permissions, 'widgets.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem
          minimizes={minimizes}
          iconClass="icon-widget"
          label={gettext('Widgets')}
          path="/widgets"
          userTipName="sidebar.widgets"
        />
      )
    },
    requests: {
      name: 'requests',
      settings: settings?.requests,
      allowed: get(permissions, 'requests.show'),
      component: <RequestNavItem minimizes={minimizes} />
    },
    suppliers: {
      name: 'suppliers',
      settings: settings?.suppliers,
      allowed: get(permissions, 'suppliers.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem
          minimizes={minimizes}
          iconClass="icon-suppliers"
          label={gettext('Suppliers')}
          path="/suppliers"
          userTipName="sidebar.suppliers"
        />
      )
    },
    analytics: {
      name: 'analytics',
      settings: settings?.analytics,
      allowed: get(permissions, 'analytics.show') && !isEmpty(settings?.analytics?.redirect_to),
      component: (
        <NavLinkItem
          minimizes={minimizes}
          iconClass="icon-line-chart"
          path=""
          href={settings?.analytics?.redirect_to || ''}
          label={gettext('Analytics')}
          // userTipName="sidebar.analytics"
        />
      )
    },

    autoCollect: {
      name: 'portal',
      settings: settings?.portal,
      allowed: get(permissions, 'portal.show') && !!settings?.portal?.visible,
      component: (
        <NavLinkItem
          minimizes={minimizes}
          iconClass="icon-global"
          path=""
          href={`${process.env.REACT_APP_PORTAL_URL}?CORE_JWT=${value || ''}`}
          label={gettext('Portal')}
          userTipName="sidebar.portal"
        />
      )
    },
    inventorySettings: {
      name: 'inventory/settings',
      settings: settings?.inventory,
      allowed: get(settings, 'inventory.visible'),
      component: (
        <NavLinkItem minimizes={minimizes} iconClass="icon-setting" path="/inventory/settings" label={gettext('Settings')} />
      )
    },
    inventoryTransactions: {
      name: 'inventory/transactions',
      settings: settings?.inventory,
      allowed: get(settings, 'inventory.visible'),
      component: (
        <NavLinkItem
          minimizes={minimizes}
          iconClass="icon-unordered-list"
          path="/inventory/transactions"
          label={gettext('Transactions')}
        />
      )
    },
    inventoryDashboard: {
      name: 'inventory/dashboard',
      settings: settings?.inventory,
      allowed: get(settings, 'inventory.visible'),
      component: (
        <NavLinkItem minimizes={minimizes} iconClass="icon-bar-chart" path="/inventory/dashboard" label={gettext('Dashboard')} />
      )
    }
  };

  const structure: SidebarStructure = [
    { type: 'single', item: menuItems.orders },
    {
      type: 'grouped',
      title: gettext('Delivery Section'),
      openByDefault: true,
      items: [menuItems.schedule, menuItems.drivers, menuItems.deliveries, menuItems.trucks]
    },
    { type: 'single', item: menuItems.finance },
    { type: 'single', item: menuItems.projects },
    { type: 'single', item: menuItems.pallets },
    { type: 'single', item: menuItems.contractors },
    { type: 'single', item: menuItems.suppliers },
    { type: 'single', item: menuItems.messages },
    {
      type: 'grouped',
      title: gettext('Settings'),
      openByDefault: false,
      items: [
        menuItems.locations,
        menuItems.products,
        menuItems.availability,
        menuItems.tax,
        menuItems.widgets,
        menuItems.requests
      ]
    },
    {
      type: 'grouped',
      title: gettext('Inventory'),
      openByDefault: false,
      items: [menuItems.inventoryDashboard, menuItems.inventoryTransactions, menuItems.inventorySettings]
    },
    { type: 'single', item: menuItems.analytics },
    { type: 'single', item: menuItems.autoCollect },
    { type: 'single', item: menuItems.profile }
  ];

  return structure;
};
